import React, { useState, useEffect, useRef } from 'react';
import VirtualKeyboard from './VirtualKeyboard';
import metricsTracker from '../utils/metricsTracker';

const MultiplicationExam = ({ updateMetrics }) => {
  // Exam state
  const [examQuestions, setExamQuestions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsPerPage, setQuestionsPerPage] = useState(20);
  const [examStarted, setExamStarted] = useState(false);
  const [examCompleted, setExamCompleted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [examProgress, setExamProgress] = useState({});
  const [examConfig, setExamConfig] = useState({
    questionCount: 2,
    gradeLevel: '1',
    operation: 'multiplication',
    useTimer: false,
    timeLimit: 30
  });
  
  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [timerActive, setTimerActive] = useState(false);

  // Virtual keyboard state
  const [keyboardVisible, setKeyboardVisible] = useState(true);
  const [keyboardPosition, setKeyboardPosition] = useState({ x: 400, y: 100 });
  const [activeInput, setActiveInput] = useState(null);
  const mathProblemRefs = useRef({});

  // Calculate total pages for pagination
  const totalPages = Math.ceil(totalQuestions / questionsPerPage);

  // Level difficulty ranges - properly aligned with "Upto X digits"
  const gradeRanges = {
    '1': { min: 1, max: 99 },        // Upto 2 digits
    '2': { min: 10, max: 999 },      // Upto 3 digits
    '3': { min: 100, max: 9999 },    // Upto 4 digits
    '4': { min: 1000, max: 99999 },  // Upto 5 digits
    '5': { min: 10000, max: 999999 }, // Upto 6 digits
    '6': { min: 100000, max: 9999999 }, // Upto 7 digits
    '7': { min: 1000000, max: 99999999 }, // Upto 8 digits
    '8': { min: 10000000, max: 999999999 } // Upto 9 digits
  };

  // Calculate paper savings
  const calculatePaperSavings = () => {
    const linesPerQuestion = 5; // Each multiplication question takes about 5 lines
    const validTotalQuestions = Number.isFinite(totalQuestions) ? totalQuestions : 0;
    const totalLines = validTotalQuestions * linesPerQuestion;
    const papersSaved = Math.ceil(totalLines / 25); // 25 lines per A4 sheet
    return { papersSaved, totalLines };
  };

  // Calculate the required length for answer
  const calculateAnswerLength = (num1, num2) => {
    const a = parseInt(num1) || 0;
    const b = parseInt(num2) || 0;
    const result = a * b;
    return result.toString().length;
  };

  // Timer functions
  const formatTime = (seconds) => {
    // Handle invalid input
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const startTimer = () => {
    const totalSeconds = examConfig.timeLimit * 60;
    setTimeRemaining(totalSeconds);
    setTimerActive(true);
  };

  const stopTimer = () => {
    setTimerActive(false);
  };

  // Generate exam questions for multiplication
  const generateExam = () => {
    const questions = [];
    const range = gradeRanges[examConfig.gradeLevel];
    
    for (let i = 0; i < examConfig.questionCount; i++) {
      const num1 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      const num2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;
      const correctAnswer = num1 * num2;
      const answerLength = calculateAnswerLength(num1, num2);
      
      questions.push({
        id: i + 1,
        num1: num1.toString(),
        num2: num2.toString(),
        correctAnswer: correctAnswer.toString(),
        userAnswer: [],
        partialProducts: [],
        carryOver: [],
        isCorrect: null,
        answerLength: answerLength
      });
    }
    
    setExamQuestions(questions);
    setTotalQuestions(questions.length);
    setExamStarted(true);
    setExamCompleted(false);
    setShowResults(false);
    setCurrentPage(1);
    setExamProgress({});
    
    // Track exam started
    metricsTracker.trackExamStarted('multiplication', examConfig.gradeLevel, examConfig.questionCount);
    
    if (examConfig.useTimer) {
      startTimer();
    }
    
    // Report metrics for new exam
    if (updateMetrics) {
      const { papersSaved } = calculatePaperSavings();
      updateMetrics(papersSaved);
    }
  };

  // Generate explanation for multiplication
  const generateExplanation = (num1, num2, result) => {
    return `To solve ${num1} × ${num2}, multiply each digit of ${num2} by ${num1}, then add all partial products. The answer is ${result}.`;
  };

  // Handle answer input changes - using proven array-based approach from addition exam
  const handleAnswerChange = (questionId, index, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      setExamQuestions(prev => prev.map(q => {
        if (q.id === questionId) {
          const newUserAnswer = [...(q.userAnswer || [])];
          newUserAnswer[index] = value;
          return { ...q, userAnswer: newUserAnswer };
        }
        return q;
      }));
    }
  };

  // Handle partial product input
  const handlePartialProductChange = (questionId, rowIndex, colIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      setExamQuestions(prev => prev.map(q => {
        if (q.id === questionId) {
          const newPartialProducts = [...(q.partialProducts || [])];
          if (!newPartialProducts[rowIndex]) {
            newPartialProducts[rowIndex] = [];
          }
          newPartialProducts[rowIndex][colIndex] = value;
          return { ...q, partialProducts: newPartialProducts };
        }
        return q;
      }));
    }
  };

  // Handle carry over input - using proven array-based approach from addition exam
  const handleCarryChange = (questionId, colIndex, value) => {
    if (value.length <= 1 && /^\d*$/.test(value)) {
      setExamQuestions(prev => prev.map(q => {
        if (q.id === questionId) {
          const newCarry = [...(q.carryOver || [])];
          newCarry[colIndex] = value;
          return { ...q, carryOver: newCarry };
        }
        return q;
      }));
    }
  };

  // Submit exam
  const submitExam = () => {
    const updatedQuestions = examQuestions.map(q => {
      const userAnswerString = (q.userAnswer || []).join('');
      const isCorrect = userAnswerString === q.correctAnswer;
      return { ...q, isCorrect };
    });
    
    // Calculate score
    const correctAnswers = updatedQuestions.filter(q => q.isCorrect).length;
    const score = Math.round((correctAnswers / updatedQuestions.length) * 100);
    
    setExamQuestions(updatedQuestions);
    setExamCompleted(true);
    setShowResults(true);
    stopTimer();
    
    // Track exam submitted
    metricsTracker.trackExamSubmitted('multiplication', examConfig.gradeLevel, score, updatedQuestions.length, correctAnswers);
  };

  // Print exam
  const printExam = () => {
    // Track print workbook
    metricsTracker.trackPrintWorkbook('multiplication', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    const printContent = generatePrintContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>BellMaths Exam - Questions and Answers</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .summary {
              margin: 30px 0;
              padding: 20px;
              background-color: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #e9ecef;
            }
            .summary h3 {
              margin: 0 0 15px 0;
              color: #667eea;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
              gap: 15px;
              margin-bottom: 20px;
            }
            .summary-item {
              padding: 10px;
              background-color: white;
              border-radius: 6px;
              border: 1px solid #e9ecef;
            }
            .summary-label {
              font-weight: bold;
              color: #667eea;
            }
            .summary-value {
              margin-left: 10px;
              font-weight: bold;
            }
            .question { 
              margin-bottom: 40px; 
              page-break-inside: avoid; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .math-problem {
              margin: 20px 0;
            }
            .problem-grid {
              display: grid;
              gap: 0px;
              align-items: center;
              margin-bottom: 20px;
            }
            .digit-input {
              width: 40px;
              height: 50px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
            }
            .answer-input {
              width: 20px;
              height: 30px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: none;
              border-radius: 0;
              background-color: transparent;
              outline: none;
              position: relative;
            }
            .operator {
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #333;
            }
            .answer-line {
              border-bottom: 2px solid #333;
              height: 30px;
              margin: 10px 0;
            }
            .solution { 
              margin-top: 20px; 
              padding: 15px; 
              background-color: #f8f9fa; 
              border-left: 4px solid #007bff; 
            }
            .solution h4 { 
              margin-top: 0; 
              color: #007bff; 
            }
            .solution-steps { 
              margin-top: 15px; 
            }
            .problem-steps, .partial-products { 
              margin-bottom: 15px; 
              padding: 15px; 
              background-color: white; 
              border-radius: 4px; 
              border: 1px solid #e9ecef; 
            }
            .steps-list, .products-list { 
              margin-top: 10px; 
              font-family: monospace; 
              font-size: 14px; 
            }
            .step-item, .product-item { 
              margin-bottom: 5px; 
            }
            .calculation { 
              color: #333; 
              background-color: #e8f4fd; 
              padding: 2px 4px; 
              border-radius: 3px; 
            }
            .result { 
              color: #155724; 
              background-color: #d4edda; 
              padding: 2px 4px; 
              border-radius: 3px; 
            }
            .explanation, .place-value { 
              color: #666; 
              font-size: 12px; 
            }
            .product-item { 
              text-align: right; 
            }
            .final-line { 
              border-top: 2px solid #333; 
              margin-top: 5px; 
              padding-top: 5px; 
              text-align: right; 
              font-weight: bold; 
            }
            .final-answer { 
              padding: 12px; 
              background-color: #007bff; 
              color: white; 
              border-radius: 6px; 
              font-weight: bold; 
              text-align: center; 
              margin-top: 15px; 
            }
            .page-break {
              page-break-before: always;
            }
            @media print {
              body { background-color: white; }
              .question { box-shadow: none; border: 1px solid #ddd; }
              .header { box-shadow: none; border: 1px solid #ddd; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  // Generate print content
  const generatePrintContent = () => {
    const padNumber = (num, length) => num.padStart(length, ' ');
    
    const results = calculateResults();
    
    let content = `
      <div class="header">
        <h1>BellMaths - Multiplication Exam</h1>
        <p>Grade Level: ${examConfig.gradeLevel} | Total Questions: ${examQuestions.length}</p>
        <p>Date: ${new Date().toLocaleDateString()}</p>
        <p>Student Name: _________________</p>
        <p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>
      </div>
      
      <!-- Exam Results Summary -->
      <div class="summary">
        <h3>Exam Results Summary</h3>
                    <div class="summary-grid">
              <div class="summary-item">
                <span class="summary-label">Correct Answers:</span>
                <span class="summary-value">${results.correctAnswers}/${results.totalQuestions}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Percentage:</span>
                <span class="summary-value">${results.percentage}%</span>
              </div>
            </div>
      </div>
    `;
    
    examQuestions.forEach((question, index) => {
      const maxLength = Math.max(question.num1.length, question.num2.length);
      const answerLength = question.answerLength;
      const multiplierDigits = question.num2.split('').reverse();
      
      // Generate solution steps
      const multiplicand = parseInt(question.num1);
      const multiplier = parseInt(question.num2);
      const steps = [];
      let partialProducts = [];
      
      // Generate partial products with proper place values
      for (let i = 0; i < multiplierDigits.length; i++) {
        const digit = parseInt(multiplierDigits[i]);
        const partialProduct = multiplicand * digit;
        const placeValue = i;
        const partialProductWithZeros = partialProduct * Math.pow(10, placeValue);
        
        steps.push({
          step: i + 1,
          multiplierDigit: digit,
          calculation: `${multiplicand} × ${digit} = ${partialProduct}`,
          partialProduct: partialProduct,
          position: i,
          placeValue: placeValue,
          partialProductWithZeros: partialProductWithZeros,
          explanation: `Step ${i + 1}: Multiply ${multiplicand} by ${digit} (${placeValue === 0 ? 'ones' : placeValue === 1 ? 'tens' : placeValue === 2 ? 'hundreds' : `${placeValue}th place`})`
        });
        
        partialProducts.push({
          value: partialProductWithZeros,
          original: partialProduct,
          placeValue: placeValue
        });
      }
      
      // Calculate final result
      const finalResult = partialProducts.reduce((sum, partial) => sum + partial.value, 0);
      
      content += `
        <div class="question">
          <div class="question-header">
            <h3>Question ${question.id}</h3>
          </div>
          
          <!-- Math Problem Section -->
          <div class="math-problem">
            <div class="problem-grid" style="grid-template-columns: 40px repeat(${maxLength}, 40px) auto;">
              <!-- Multiplicand row -->
              <div></div>
      `;
      
      // Multiplicand inputs
      const paddedNum1 = padNumber(question.num1, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum1[i] === ' ' ? '' : paddedNum1[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Multiplier row with operator
      content += `<div class="operator">×</div>`;
      const paddedNum2 = padNumber(question.num2, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum2[i] === ' ' ? '' : paddedNum2[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Line row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<div class="answer-line"></div>`;
      }
      content += `<div></div>`;
      
      // Answer row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<input type="text" class="answer-input" maxlength="1" />`;
      }
      content += `<div></div>`;
      
      content += `
            </div>
          </div>
          
          <!-- Solution Steps -->
          <div class="solution">
            <h4>Step-by-Step Solution</h4>
            <div class="solution-steps">
              <!-- Problem and Steps Combined -->
              <div class="problem-steps">
                <strong>Problem: ${question.num1} × ${question.num2} = ?</strong>
                <div class="steps-list">
                  ${steps.map(step => `
                    <div class="step-item">
                      <span class="calculation">${step.calculation}</span>
                      <span class="result"> = ${step.partialProduct}</span>
                      <span class="explanation"> (${step.explanation.split(': ')[1]})</span>
                    </div>
                  `).join('')}
                </div>
              </div>

              <!-- Partial Products and Addition Combined -->
              <div class="partial-products">
                <strong>Partial Products with Place Values:</strong>
                <div class="products-list">
                  ${partialProducts.map(partial => `
                    <div class="product-item">
                      ${partial.value.toString().padStart(finalResult.toString().length, ' ')}
                      <span class="place-value"> (${partial.original} × 10^${partial.placeValue})</span>
                    </div>
                  `).join('')}
                  <div class="final-line">
                    ${finalResult}
                  </div>
                </div>
              </div>

              <!-- Final Answer -->
              <div class="final-answer">
                Final Answer: ${finalResult}
              </div>
            </div>
          </div>
        </div>
      `;
    });
    
    content += `
      <div class="footer">
        <p>Name: _________________ Date: _________________</p>
        <p>Score: _____ / ${examQuestions.length}</p>
      </div>
    `;
    
    return content;
  };

  // Generate questions only content
  const generateQuestionsOnlyContent = () => {
    const padNumber = (num, length) => num.padStart(length, ' ');
    
    let content = `
      <div class="header">
        <h1>BellMaths - Multiplication Exam</h1>
        <p>Grade Level: ${examConfig.gradeLevel} | Total Questions: ${examQuestions.length}</p>
        <p>Date: ${new Date().toLocaleDateString()}</p>
        <p>Student Name: _________________</p>
        <p style="color: #28a745; font-style: italic;">🌱 Eco-Friendly: This digital exam saves paper and helps protect our environment!</p>
      </div>
    `;

    examQuestions.forEach((question, index) => {
      if (index > 0 && index % 5 === 0) {
        content += '<div class="page-break"></div>';
      }
      
      const maxLength = Math.max(question.num1.length, question.num2.length);
      
      content += `
        <div class="question">
          <div class="question-header">
            <h3>Question ${question.id}</h3>
          </div>
          
          <!-- Math Problem Section -->
          <div class="math-problem">
            <div class="problem-grid" style="grid-template-columns: 40px repeat(${maxLength}, 40px) auto;">
              <!-- Multiplicand row -->
              <div></div>
      `;
      
      // Multiplicand inputs
      const paddedNum1 = padNumber(question.num1, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum1[i] === ' ' ? '' : paddedNum1[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Multiplier row with operator
      content += `<div class="operator">×</div>`;
      const paddedNum2 = padNumber(question.num2, maxLength);
      for (let i = 0; i < maxLength; i++) {
        const digit = paddedNum2[i] === ' ' ? '' : paddedNum2[i];
        content += `<input type="text" class="digit-input" value="${digit}" readonly />`;
      }
      content += `<div></div>`;
      
      // Line row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<div class="answer-line"></div>`;
      }
      content += `<div></div>`;
      
      // Answer row
      content += `<div></div>`;
      for (let i = 0; i < maxLength; i++) {
        content += `<input type="text" class="answer-input" maxlength="1" />`;
      }
      content += `<div></div>`;
      
      content += `
            </div>
          </div>
        </div>
      `;
    });

    return content;
  };

  // Print questions only
  const printQuestionsOnly = () => {
    // Track print questions
    metricsTracker.trackPrintQuestions('multiplication', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    const printContent = generateQuestionsOnlyContent();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>BellMaths Exam - Questions Only</title>
          <style>
            body { 
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
              margin: 20px; 
              background-color: #f5f5f5;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header h1 {
              color: #667eea;
              margin: 0 0 10px 0;
            }
            .header p {
              margin: 5px 0;
              color: #666;
            }
            .question { 
              margin-bottom: 40px; 
              page-break-inside: avoid; 
              background-color: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .question-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 15px;
              padding-bottom: 10px;
              border-bottom: 2px solid #e9ecef;
            }
            .question-header h3 {
              margin: 0;
              color: #667eea;
            }
            .math-problem {
              margin: 20px 0;
            }
            .problem-grid {
              display: grid;
              gap: 0px;
              align-items: center;
              margin-bottom: 20px;
            }
            .digit-input {
              width: 40px;
              height: 50px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: 2px solid #ddd;
              border-radius: 4px;
              background-color: #e8f4fd;
            }
            .answer-input {
              width: 20px;
              height: 30px;
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              border: none;
              border-radius: 0;
              background-color: transparent;
              outline: none;
              position: relative;
            }
            .operator {
              text-align: center;
              font-size: 1.5rem;
              font-weight: bold;
              color: #333;
            }
            .answer-line {
              border-bottom: 2px solid #333;
              height: 30px;
              margin: 10px 0;
            }
            .page-break {
              page-break-before: always;
            }
            @media print {
              body { background-color: white; }
              .question { box-shadow: none; border: 1px solid #ddd; }
              .header { box-shadow: none; border: 1px solid #ddd; }
            }
          </style>
        </head>
        <body>
          ${printContent}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  // Print plain questions (simple format without BellMaths UI design)
  const printPlainQuestions = () => {
    // Track print answers
    metricsTracker.trackPrintAnswers('multiplication', examConfig.gradeLevel, totalQuestions);
    
    const printWindow = window.open('', '_blank');
    
    let plainContent = `
      <html>
        <head>
          <title>Math Exam - Questions</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            .header { 
              text-align: center; 
              margin-bottom: 30px; 
              border-bottom: 2px solid #333;
              padding-bottom: 20px;
            }
            .header h1 {
              margin: 0 0 10px 0;
              color: #333;
            }
            .question { 
              margin-bottom: 30px; 
              page-break-inside: avoid;
            }
            .question-number {
              font-weight: bold;
              margin-bottom: 10px;
              color: #333;
            }
            .math-problem {
              font-size: 18px;
              margin: 15px 0;
              padding: 15px;
              border: 1px solid #ddd;
              background-color: #f9f9f9;
            }
            .answer-space {
              margin-top: 20px;
              padding: 10px;
              border: 1px solid #ccc;
              background-color: #fff;
              min-height: 40px;
            }
            @media print {
              body { margin: 15px; }
              .question { margin-bottom: 25px; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Math Exam</h1>
            <p>Student Name: _________________</p>
            <p>Date: _________________</p>
            <p>Total Questions: ${examQuestions.length}</p>
          </div>
    `;

    examQuestions.forEach((question, index) => {
      plainContent += `
        <div class="question">
          <div class="question-number">Question ${index + 1}</div>
          <div class="math-problem">
            ${question.num1} × ${question.num2} = ?
          </div>
          <div class="answer-space">
            Answer: _________________
          </div>
        </div>
      `;
    });

    plainContent += `
        </body>
      </html>
    `;
    
    printWindow.document.write(plainContent);
    printWindow.document.close();
    printWindow.print();
  };

  // Calculate results
  const calculateResults = () => {
    const correctAnswers = examQuestions.filter(q => q.isCorrect).length;
    const totalQuestions = examQuestions.length;
    const percentage = totalQuestions > 0 ? (correctAnswers / totalQuestions) * 100 : 0;
    
    return {
      correctAnswers,
      totalQuestions,
      percentage: percentage.toFixed(1)
    };
  };

  // Pad number with spaces for display
  const padNumber = (num, length) => {
    return num.toString().padStart(length, ' ');
  };

  const results = calculateResults();

  // Handle input focus for virtual keyboard - using proven logic from addition exam
  const handleInputFocus = (index, type, questionIndex, rowIndex = null) => {
    console.log('Exam handleInputFocus:', { index, type, questionIndex, rowIndex });
    setActiveInput({ index, type, questionIndex, rowIndex });
    setKeyboardVisible(true);
    
    // Position keyboard just next to the input
    let inputElement;
    if (type === 'partial') {
      // For partial products, the data-index includes rowIndex
      inputElement = document.querySelector(`input[data-question="${questionIndex}"][data-index="partial-${rowIndex}-${index}"][data-type="partial"]`);
    } else {
      // For answer and carry fields, use the standard format
      inputElement = document.querySelector(`input[data-question="${questionIndex}"][data-index="${type}-${index}"][data-type="${type}"]`);
    }
    console.log('Found input element:', inputElement);
    
    if (inputElement) {
      const rect = inputElement.getBoundingClientRect();
      const mathProblemRect = mathProblemRefs.current[questionIndex]?.getBoundingClientRect();
      
      console.log('Positioning debug:', {
        inputRect: rect,
        mathProblemRect: mathProblemRect,
        mathProblemRefs: mathProblemRefs.current
      });
      
      if (mathProblemRect) {
        // Calculate position based on place value - move left for higher place values
        const question = examQuestions.find(q => q.id === questionIndex);
        const maxLength = Math.max(question.num1.length, question.num2.length);
        const placeValue = maxLength - index - 1; // 0 = ones, 1 = tens, 2 = hundreds, etc.
        
        // Base position: just next to input (5px offset)
        let x = rect.right - mathProblemRect.left + 5;
        
        // Move left for higher place values to avoid hiding questions
        if (placeValue >= 1) { // tens or higher
          x -= (placeValue * 15); // Move left by 15px per place value
        }
        
        // Ensure keyboard doesn't go too far left
        x = Math.max(x, 50);
        
        // Move keyboard down to avoid hiding the second number
        let y = rect.top - mathProblemRect.top;
        
        // If this is a carry field, move keyboard down more to avoid hiding first number
        if (type === 'carry') {
          y += 60; // Move down by 60px for carry fields
        } else if (type === 'partial') {
          // For partial products, position below the field to avoid hiding the problem
          y += 80; // Move down by 80px for partial products
        } else {
          y += 40; // Move down by 40px for answer fields to avoid hiding second number
        }
        
        console.log('Setting keyboard position:', { x, y });
        setKeyboardPosition({ x, y });
      } else {
        console.log('mathProblemRect is null!');
      }
    }
  };

  const handleInputBlur = () => {
    // Don't hide keyboard, don't clear active input to allow virtual keyboard input
  };

  // Handle virtual keyboard input
  const handleVirtualKeyPress = (key) => {
    console.log('Virtual key press:', key, 'Active input:', activeInput);
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Updating answer at index:', activeInput.index, 'with value:', key);
        handleAnswerChange(activeInput.questionIndex, activeInput.index, key);
      } else if (activeInput.type === 'carry') {
        console.log('Updating carry at index:', activeInput.index, 'with value:', key);
        handleCarryChange(activeInput.questionIndex, activeInput.index, key);
      } else if (activeInput.type === 'partial') {
        console.log('Updating partial at index:', activeInput.index, 'with value:', key);
        handlePartialProductChange(activeInput.questionIndex, activeInput.rowIndex, activeInput.index, key);
      }
      
      // Re-focus the active input field after virtual keyboard input
      setTimeout(() => {
        let inputElement;
        if (activeInput.type === 'partial') {
          // For partial products, the data-index includes rowIndex
          inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="partial-${activeInput.rowIndex}-${activeInput.index}"]`);
        } else {
          // For answer and carry fields, use the standard format
          inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="${activeInput.type}-${activeInput.index}"]`);
        }
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard backspace
  const handleVirtualBackspace = () => {
    console.log('Virtual backspace, Active input:', activeInput);
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Clearing answer at index:', activeInput.index);
        handleAnswerChange(activeInput.questionIndex, activeInput.index, '');
      } else if (activeInput.type === 'carry') {
        console.log('Clearing carry at index:', activeInput.index);
        handleCarryChange(activeInput.questionIndex, activeInput.index, '');
      } else if (activeInput.type === 'partial') {
        console.log('Clearing partial at index:', activeInput.index);
        handlePartialProductChange(activeInput.questionIndex, activeInput.rowIndex, activeInput.index, '');
      }
      
      // Re-focus the active input field after virtual keyboard backspace
      setTimeout(() => {
        let inputElement;
        if (activeInput.type === 'partial') {
          // For partial products, the data-index includes rowIndex
          inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="partial-${activeInput.rowIndex}-${activeInput.index}"]`);
        } else {
          // For answer and carry fields, use the standard format
          inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="${activeInput.type}-${activeInput.index}"]`);
        }
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle virtual keyboard clear
  const handleVirtualClear = () => {
    console.log('Virtual clear, Active input:', activeInput);
    if (activeInput) {
      if (activeInput.type === 'answer') {
        console.log('Clearing answer at index:', activeInput.index);
        handleAnswerChange(activeInput.questionIndex, activeInput.index, '');
      } else if (activeInput.type === 'carry') {
        console.log('Clearing carry at index:', activeInput.index);
        handleCarryChange(activeInput.questionIndex, activeInput.index, '');
      } else if (activeInput.type === 'partial') {
        console.log('Clearing partial at index:', activeInput.index);
        handlePartialProductChange(activeInput.questionIndex, activeInput.rowIndex, activeInput.index, '');
      }
      
      // Re-focus the active input field after virtual keyboard clear
      setTimeout(() => {
        let inputElement;
        if (activeInput.type === 'partial') {
          // For partial products, the data-index includes rowIndex
          inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="partial-${activeInput.rowIndex}-${activeInput.index}"]`);
        } else {
          // For answer and carry fields, use the standard format
          inputElement = document.querySelector(`input[data-question="${activeInput.questionIndex}"][data-index="${activeInput.type}-${activeInput.index}"]`);
        }
        if (inputElement) {
          inputElement.focus();
        }
      }, 0);
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e, currentIndex, type, questionId, rowIndex = null) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      submitExam();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      // Move to next input
      const nextIndex = currentIndex + 1;
      const question = examQuestions.find(q => q.id === questionId);
      const maxLength = question ? question.answerLength : 0;
      
      if (nextIndex < maxLength) {
        let nextInput;
        if (type === 'partial') {
          nextInput = document.querySelector(`[data-question="${questionId}"][data-index="partial-${rowIndex}-${nextIndex}"][data-type="partial"]`);
        } else {
          nextInput = document.querySelector(`[data-question="${questionId}"][data-index="${type}-${nextIndex}"][data-type="${type}"]`);
        }
        if (nextInput) nextInput.focus();
      }
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      // Move to previous input
      const prevIndex = currentIndex - 1;
      
      if (prevIndex >= 0) {
        let prevInput;
        if (type === 'partial') {
          prevInput = document.querySelector(`[data-question="${questionId}"][data-index="partial-${rowIndex}-${prevIndex}"][data-type="partial"]`);
        } else {
          prevInput = document.querySelector(`[data-question="${questionId}"][data-index="${type}-${prevIndex}"][data-type="${type}"]`);
        }
        if (prevInput) prevInput.focus();
      }
    }
  };

  // Click outside to hide keyboard
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is on virtual keyboard
      const virtualKeyboard = event.target.closest('.virtual-keyboard');
      if (virtualKeyboard) {
        return; // Don't hide keyboard if clicking on it
      }
      
      // Check if click is on an input field
      const isInputField = event.target.tagName === 'INPUT' && 
        (event.target.classList.contains('carry-input') || 
         event.target.classList.contains('partial-input') || 
         event.target.classList.contains('digit-input'));
      
      if (!isInputField) {
        console.log('Click not on input field detected, hiding keyboard');
        setKeyboardVisible(false);
        setActiveInput(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Timer effect
  useEffect(() => {
    let interval = null;
    if (timerActive && timeRemaining > 0) {
      interval = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            setTimerActive(false);
            submitExam();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timerActive, timeRemaining]);

  return (
    <div className="multiplication-exam">
             {/* Exam Setup */}
       {!examStarted && (
         <div className="exam-setup">
           <div className="page-title">
             <h2>Multiplication Exam Setup</h2>
             <p>Configure your multiplication exam settings</p>
           </div>

           <div className="exam-config">
             <div className="config-item">
               <label>Number of Questions:</label>
               <select 
                 value={examConfig.questionCount} 
                 onChange={(e) => setExamConfig(prev => ({ ...prev, questionCount: parseInt(e.target.value) }))}
               >
                 {[1, 2, 5, 10, 15, 20].map(count => (
                   <option 
                     key={count} 
                     value={count}
                     disabled={count > 2}
                     style={count > 2 ? { color: '#999', fontStyle: 'italic' } : {}}
                   >
                     {count} {count === 1 ? 'Question' : 'Questions'}
                     {count > 2 && ' (Upgrade to Pro Edition)'}
                   </option>
                 ))}
               </select>
               {examConfig.questionCount > 2 && (
                 <div style={{ 
                   marginTop: '8px', 
                   padding: '8px 12px', 
                   backgroundColor: '#fff3cd', 
                   border: '1px solid #ffeaa7', 
                   borderRadius: '4px',
                   fontSize: '14px',
                   color: '#856404'
                 }}>
                   ⚠️ Upgrade to Pro Edition to support {examConfig.questionCount} questions
               </div>
             )}
             </div>

             <div className="config-item">
               <label>Level:</label>
               <select 
                 value={examConfig.gradeLevel} 
                 onChange={(e) => setExamConfig(prev => ({ ...prev, gradeLevel: e.target.value }))}
               >
                 {Array.from({ length: 8 }, (_, i) => i + 1).map(level => (
                   <option 
                     key={level} 
                     value={level.toString()}
                     disabled={level > 3}
                     style={{ color: level > 3 ? '#999' : 'inherit' }}
                   >
                     {level > 3 ? `Level ${level} (Upto ${level + 1} digits) - Upgrade to Pro Edition` : `Level ${level} (Upto ${level + 1} digits)`}
                   </option>
                 ))}
               </select>
             </div>

             {/* Timer Configuration */}
             <div className="config-item" style={{ marginTop: '20px' }}>
               <label style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                 <input
                   type="checkbox"
                   checked={examConfig.useTimer}
                   onChange={(e) => setExamConfig(prev => ({ ...prev, useTimer: e.target.checked }))}
                 />
                 Enable Timer
               </label>
             </div>

             {examConfig.useTimer && (
               <div className="config-item">
                 <label>Time Limit (minutes):</label>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                   <input
                     type="range"
                     min="1"
                     max="180"
                     value={examConfig.timeLimit}
                     onChange={(e) => setExamConfig(prev => ({ ...prev, timeLimit: parseInt(e.target.value) }))}
                     style={{ flex: 1 }}
                   />
                   <input
                     type="number"
                     min="1"
                     max="180"
                     value={examConfig.timeLimit}
                     onChange={(e) => {
                       const value = parseInt(e.target.value);
                       if (value >= 1 && value <= 180) {
                         setExamConfig(prev => ({ ...prev, timeLimit: value }));
                       }
                     }}
                     style={{ 
                       width: '80px', 
                       padding: '5px', 
                       border: '1px solid #ccc',
                       borderRadius: '4px'
                     }}
                   />
                   <span style={{ fontSize: '14px', color: '#666' }}>
                     {examConfig.timeLimit} min
                   </span>
                 </div>
               </div>
             )}

             {/* Color Coding Guide */}
             <div style={{ 
               marginTop: '20px', 
               padding: '15px', 
               backgroundColor: '#f8f9fa', 
               borderRadius: '8px', 
               border: '1px solid #e9ecef' 
             }}>
               <h4 style={{ margin: '0 0 10px 0', color: '#667eea' }}>Long Multiplication Instructions</h4>
               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px' }}>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                   <span style={{ 
                     display: 'inline-block', 
                     width: '20px', 
                     height: '20px', 
                     backgroundColor: '#e8f4fd', 
                     border: '1px solid #ddd',
                     borderRadius: '3px'
                   }}></span>
                   <span>Multiplicand & Multiplier</span>
                 </div>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                   <span style={{ 
                     display: 'inline-block', 
                     width: '20px', 
                     height: '20px', 
                     backgroundColor: '#fff3e0', 
                     border: '1px solid #e67e22',
                     borderRadius: '3px'
                   }}></span>
                   <span>Carry Values</span>
                 </div>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                   <span style={{ 
                     display: 'inline-block', 
                     width: '20px', 
                     height: '20px', 
                     backgroundColor: 'white', 
                     border: '1px solid #ddd',
                     borderRadius: '3px'
                   }}></span>
                   <span>Partial Products</span>
                 </div>
                 <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                   <span style={{ 
                     display: 'inline-block', 
                     width: '20px', 
                     height: '20px', 
                     backgroundColor: 'transparent', 
                     border: '1px solid #ddd',
                     borderRadius: '3px'
                   }}></span>
                   <span>Final Answer</span>
                 </div>
               </div>
               <div style={{ marginTop: '15px', fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
                 <strong>How to solve:</strong>
                 <ol style={{ margin: '10px 0 0 20px', padding: '0' }}>
                   <li>Multiply each digit of the multiplier by the entire multiplicand</li>
                   <li>Write partial products with proper place values (add zeros)</li>
                   <li>Add all partial products to get the final answer</li>
                   <li>Use carry values when partial products exceed single digits</li>
                 </ol>
               </div>
             </div>

             <button className="btn btn-primary btn-large" onClick={generateExam}>
               Start Multiplication Exam
             </button>
           </div>
         </div>
       )}

             {/* Exam Interface */}
       {examStarted && (
         <>
           {/* Exam Header */}
           <div className="exam-header">
             <div className="exam-info">
               <h2>Multiplication Exam - Grade {examConfig.gradeLevel}</h2>
               <p>Question {currentPage} of {examQuestions.length}</p>
               {examConfig.useTimer && (
                 <div style={{ 
                   marginTop: '10px',
                   padding: '10px 15px',
                   backgroundColor: timeRemaining <= 300 ? '#f8d7da' : '#d4edda',
                   borderRadius: '6px',
                   border: `2px solid ${timeRemaining <= 300 ? '#dc3545' : '#28a745'}`,
                   textAlign: 'center'
                 }}>
                   <strong style={{ 
                     color: timeRemaining <= 300 ? '#721c24' : '#155724',
                     fontSize: '18px'
                   }}>
                     Time Remaining: {formatTime(timeRemaining)}
                   </strong>
                 </div>
               )}
             </div>
             
             <div className="exam-actions" style={{ 
               display: 'flex', 
               gap: '10px', 
               justifyContent: 'center', 
               marginTop: '20px',
               padding: '15px',
               backgroundColor: '#f8f9fa',
               borderRadius: '8px',
               border: '1px solid #e9ecef'
             }}>
               {!examCompleted ? (
                 <>
                   {/* Before exam submission - show both print buttons */}
                   <button className="btn btn-secondary" onClick={printQuestionsOnly}>
                     Print BellMaths Notebook
                   </button>
                   <button className="btn btn-secondary" onClick={printPlainQuestions}>
                     Print Questions
                   </button>
                 </>
               ) : (
                 <>
                   {/* After exam submission - show both print buttons */}
                   <button className="btn btn-secondary" onClick={printExam}>
                     Print BellMaths Notebook with Answers
                   </button>
                   <button className="btn btn-secondary" onClick={printPlainQuestions}>
                     Print Questions
                   </button>
                 </>
               )}
               {/* Restart Exam Button - After submission */}
               {showResults && (
                 <button 
                   className="btn btn-primary" 
                   onClick={() => {
                     setExamStarted(false);
                     setExamCompleted(false);
                     setShowResults(false);
                     setExamQuestions([]);
                     setCurrentPage(1);
                     setKeyboardVisible(false);
                     setActiveInput(null);
                     setTimeRemaining(0);
                     setTimerActive(false);
                   }}
                   style={{ 
                     padding: '10px 20px', 
                     fontSize: '14px',
                     fontWeight: 'bold'
                   }}
                 >
                   Restart Exam
                 </button>
               )}
               {!examCompleted && (
                 <button className="btn btn-success" onClick={submitExam}>
                   Submit Exam
                 </button>
               )}
             </div>
           </div>

           {/* Question Navigation Bar */}
           {!showResults && examQuestions.length > 0 && (
             <div style={{
               position: 'sticky',
               top: '0',
               zIndex: 100,
               backgroundColor: 'white',
               padding: '15px',
               borderBottom: '2px solid #e9ecef',
               marginBottom: '20px',
               boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
             }}>
               <div style={{
                 display: 'flex',
                 justifyContent: 'space-between',
                 alignItems: 'center',
                 marginBottom: '10px'
               }}>
                 <h4 style={{ margin: 0, color: '#667eea' }}>Quick Navigation</h4>
                 <span style={{ fontSize: '14px', color: '#666' }}>
                   Click any question number to jump to it
                 </span>
               </div>
               <div style={{
                 display: 'flex',
                 flexWrap: 'wrap',
                 gap: '8px',
                 maxHeight: '120px',
                 overflowY: 'auto'
               }}>
                 {examQuestions.map((question, index) => {
                   const hasAnswer = question.userAnswer && question.userAnswer.some(digit => digit);
                   return (
                     <button
                       key={question.id}
                       onClick={() => {
                         const questionElement = document.getElementById(`question-${question.id}`);
                         if (questionElement) {
                           questionElement.scrollIntoView({ 
                             behavior: 'smooth', 
                             block: 'center' 
                           });
                         }
                       }}
                       style={{
                         padding: '8px 12px',
                         border: '2px solid',
                         borderColor: hasAnswer ? '#28a745' : '#e9ecef',
                         backgroundColor: hasAnswer ? '#d4edda' : 'white',
                         color: hasAnswer ? '#155724' : '#666',
                         borderRadius: '6px',
                         cursor: 'pointer',
                         fontWeight: 'bold',
                         fontSize: '14px',
                         minWidth: '40px',
                         transition: 'all 0.2s ease'
                       }}
                       onMouseEnter={(e) => {
                         e.target.style.transform = 'scale(1.05)';
                         e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.2)';
                       }}
                       onMouseLeave={(e) => {
                         e.target.style.transform = 'scale(1)';
                         e.target.style.boxShadow = 'none';
                       }}
                     >
                       {question.id}
                     </button>
                   );
                 })}
               </div>
             </div>
           )}

           {/* Questions */}
           <div className="questions-container" style={{ display: showResults ? 'none' : 'block' }}>
            {examQuestions.map((question, index) => (
                             <div key={question.id} id={`question-${question.id}`} className="question-container">
                 <h3>Question {question.id}</h3>
                 
                 {/* Long Multiplication Layout */}
                 <div className="math-problem" ref={(el) => mathProblemRefs.current[question.id] = el} style={{ position: 'relative' }}>
                  <div style={{ 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    fontFamily: 'monospace',
                    fontSize: '18px',
                    alignItems: 'flex-end',
                    marginRight: '200px'
                  }}>
                    {/* Multiplicand row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '30px' }}></div>
                      {padNumber(question.num1, Math.max(question.num1.length, question.num2.length)).split('').map((digit, digitIndex) => (
                        <div
                          key={`num1-${digitIndex}`}
                          style={{
                            width: '40px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#e8f4fd',
                            border: '2px solid #ddd',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                          }}
                        >
                          {digit === ' ' ? '' : digit}
                        </div>
                      ))}
                    </div>

                    {/* Multiplier row with operator */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ 
                        width: '30px', 
                        textAlign: 'center',
                        fontSize: '24px',
                        fontWeight: 'bold'
                      }}>×</div>
                      {padNumber(question.num2, Math.max(question.num1.length, question.num2.length)).split('').map((digit, digitIndex) => (
                        <div
                          key={`num2-${digitIndex}`}
                          style={{
                            width: '40px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#e8f4fd',
                            border: '2px solid #ddd',
                            borderRadius: '4px',
                            fontWeight: 'bold'
                          }}
                        >
                          {digit === ' ' ? '' : digit}
                        </div>
                      ))}
                    </div>

                    {/* Line */}
                    <div style={{ 
                      height: '2px', 
                      backgroundColor: '#333',
                      margin: '10px 0',
                      width: `${Math.max(question.num1.length, question.num2.length) * 40 + (Math.max(question.num1.length, question.num2.length) - 1) * 5}px`
                    }}></div>

                                         {/* Single Carry-over row above first partial product */}
                     {question.num2.split('').reverse().length > 0 && (
                       <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                         <div style={{ 
                           width: '60px', 
                           textAlign: 'center',
                           fontSize: '12px',
                           fontWeight: 'bold',
                           color: '#e67e22'
                         }}>Carry</div>
                         {Array.from({ length: question.answerLength }, (_, colIndex) => (
                           <div key={`carry-${colIndex}`} style={{ width: '40px', height: '25px' }}>
                             {colIndex < question.answerLength - 1 && (
                               <input
                                 type="text"
                                 className="carry-input"
                                 value={question.carryOver[colIndex] || ''}
                                 onChange={(e) => handleCarryChange(question.id, colIndex, e.target.value)}
                                 onFocus={(e) => handleInputFocus(colIndex, 'carry', question.id)}
                                 onBlur={handleInputBlur}
                                 onKeyDown={(e) => handleKeyDown(e, colIndex, 'carry', question.id)}
                                                                   data-question={question.id}
                                  data-index={`carry-${colIndex}`}
                                  data-type="carry"
                                 placeholder=""
                                 style={{
                                   width: '30px',
                                   height: '20px',
                                   fontSize: '12px',
                                   textAlign: 'center',
                                   border: 'none',
                                   borderBottom: '2px solid #e67e22',
                                   borderRadius: '0',
                                   backgroundColor: '#fff3e0',
                                   color: '#e67e22',
                                   fontWeight: 'bold'
                                 }}
                               />
                             )}
                           </div>
                         ))}
                       </div>
                     )}

                                         {/* Partial Products Rows - Right Aligned with Diagonal Growth */}
                     {question.num2.split('').reverse().map((digit, rowIndex) => {
                       const leftPadding = rowIndex * 40; // Each row shifts left by one digit width
                       return (
                         <div key={`partial-row-${rowIndex}`} style={{ 
                           display: 'flex', 
                           alignItems: 'center', 
                           gap: '5px',
                           marginLeft: `${leftPadding}px` // Shift left for diagonal growth
                         }}>
                           <div style={{ width: '30px' }}></div> {/* Space for operator */}
                           {Array.from({ length: question.answerLength }, (_, colIndex) => (
                             <div key={`partial-${rowIndex}-${colIndex}`} style={{ width: '40px', height: '50px' }}>
                               <input
                                 type="text"
                                 className="partial-input"
                                 value={question.partialProducts[rowIndex] && question.partialProducts[rowIndex][colIndex] ? question.partialProducts[rowIndex][colIndex] : ''}
                                 onChange={(e) => handlePartialProductChange(question.id, rowIndex, colIndex, e.target.value)}
                                                                   onFocus={(e) => handleInputFocus(colIndex, 'partial', question.id, rowIndex)}
                                  onBlur={handleInputBlur}
                                  onKeyDown={(e) => handleKeyDown(e, colIndex, 'partial', question.id, rowIndex)}
                                                                    data-question={question.id}
                                   data-index={`partial-${rowIndex}-${colIndex}`}
                                   data-type="partial"
                                 placeholder=""
                                 style={{
                                   width: '40px',
                                   height: '50px',
                                   textAlign: 'center',
                                   fontSize: '18px',
                                   fontWeight: 'bold',
                                   border: '2px solid #ddd',
                                   borderRadius: '4px',
                                   backgroundColor: 'white'
                                 }}
                               />
                             </div>
                           ))}
                         </div>
                       );
                     })}

                    {/* Final line */}
                    <div style={{ 
                      height: '2px', 
                      backgroundColor: '#333',
                      margin: '10px 0',
                      width: `${question.answerLength * 40 + (question.answerLength - 1) * 5}px`
                    }}></div>

                    {/* Answer row */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <div style={{ width: '30px' }}></div>
                      {Array.from({ length: question.answerLength }, (_, index) => (
                        <div key={`answer-container-${index}`} style={{ 
                          width: '40px', 
                          height: '50px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          position: 'relative'
                        }}>
                          <input
                            key={`answer-${index}`}
                            type="text"
                            className={`digit-input ${question.isCorrect !== null ? 
                              (question.userAnswer[index] === question.correctAnswer.padStart(question.answerLength, '0')[index] ? 'correct' : 'incorrect') : ''}`}
                            value={question.userAnswer[index] || ''}
                            onChange={(e) => handleAnswerChange(question.id, index, e.target.value)}
                            onFocus={(e) => handleInputFocus(index, 'answer', question.id)}
                            onBlur={handleInputBlur}
                            onKeyDown={(e) => handleKeyDown(e, index, 'answer', question.id)}
                                                         data-question={question.id}
                             data-index={`answer-${index}`}
                             data-type="answer"
                            maxLength={1}
                            placeholder=""
                            style={{
                              width: '40px',
                              height: '50px',
                              textAlign: 'center',
                              fontSize: '18px',
                              fontWeight: 'bold',
                              border: '2px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: 'transparent',
                              transition: 'border-color 0.3s ease',
                              outline: 'none'
                            }}
                          />
                          {/* Dash underline */}
                          <div style={{
                            position: 'absolute',
                            bottom: '5px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '25px',
                            height: '3px',
                            backgroundColor: '#333',
                            borderRadius: '1px'
                          }}></div>
                        </div>
                      ))}
                                         </div>
                                      </div>
                  
                  {/* Virtual Keyboard - Only show for this question if it's active */}
                  {activeInput && activeInput.questionIndex === question.id && keyboardVisible && (
                    <VirtualKeyboard
                      onKeyPress={handleVirtualKeyPress}
                      onBackspace={handleVirtualBackspace}
                      onClear={handleVirtualClear}
                      position={keyboardPosition}
                      isVisible={keyboardVisible}
                    />
                  )}
                </div>
               </div>
             ))}
           </div>

           {/* Pagination */}
           {examStarted && (
             <div className="exam-pagination">
               <button 
                 className="btn btn-secondary" 
                 onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                 disabled={currentPage === 1}
               >
                 Previous
               </button>
               
               <span className="page-info">
                 Page {currentPage} of {totalPages}
               </span>
               
               <button 
                 className="btn btn-secondary" 
                 onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                 disabled={currentPage === totalPages}
               >
                 Next
               </button>
             </div>
           )}

           {/* Bottom Submit Button */}
           {examStarted && !examCompleted && (
             <div style={{ 
               textAlign: 'center', 
               marginTop: '30px', 
               padding: '20px',
               borderTop: '2px solid #e9ecef'
             }}>
               <button className="btn btn-success btn-large" onClick={submitExam}>
                 Submit Exam
               </button>
             </div>
           )}

        </>
      )}

      {/* Questions */}
      <div className="questions-container">
        {/* Exam Results Summary - Positioned above question 1 as requested */}
        {showResults && (
          <div className="exam-results" style={{ 
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '2px solid #667eea'
          }}>
            <h3 style={{ color: '#667eea', marginBottom: '15px' }}>Exam Results Summary</h3>
            <div className="results-summary" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '15px',
              marginBottom: '20px'
            }}>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Correct Answers:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{results.correctAnswers}/{results.totalQuestions}</span>
              </div>
              <div className="result-item" style={{ 
                padding: '10px',
                backgroundColor: 'white',
                borderRadius: '6px',
                border: '1px solid #e9ecef'
              }}>
                <span className="result-label" style={{ fontWeight: 'bold', color: '#667eea' }}>Percentage:</span>
                <span className="result-value" style={{ marginLeft: '10px', fontWeight: 'bold' }}>{results.percentage}%</span>
              </div>
            </div>
          </div>
        )}
        
        {showResults && examQuestions.map((question, index) => {
          const maxLength = Math.max(question.num1.length, question.num2.length, question.correctAnswer.length);
          const padNumber = (num, length) => num.padStart(length, ' ');
          
          // Generate solution steps for this question
          const multiplicand = parseInt(question.num1);
          const multiplier = parseInt(question.num2);
          const multiplierDigits = question.num2.split('').reverse();
          const steps = [];
          let partialProducts = [];
          
          // Generate partial products with proper place values
          for (let i = 0; i < multiplierDigits.length; i++) {
            const digit = parseInt(multiplierDigits[i]);
            const partialProduct = multiplicand * digit;
            const placeValue = i;
            const partialProductWithZeros = partialProduct * Math.pow(10, placeValue);
            
            steps.push({
              step: i + 1,
              multiplierDigit: digit,
              calculation: `${multiplicand} × ${digit} = ${partialProduct}`,
              partialProduct: partialProduct,
              position: i,
              placeValue: placeValue,
              partialProductWithZeros: partialProductWithZeros,
              explanation: `Step ${i + 1}: Multiply ${multiplicand} by ${digit} (${placeValue === 0 ? 'ones' : placeValue === 1 ? 'tens' : placeValue === 2 ? 'hundreds' : `${placeValue}th place`})`
            });
            
            partialProducts.push({
              value: partialProductWithZeros,
              original: partialProduct,
              placeValue: placeValue
            });
          }
          
          // Calculate final result
          const finalResult = partialProducts.reduce((sum, partial) => sum + partial.value, 0);
          
          return (
            <div key={question.id} className="question-container">
              <div className="question-header">
                <h3>Question {question.id}</h3>
                {examCompleted && (
                  <span className={`result-badge ${question.isCorrect ? 'correct' : 'incorrect'}`}>
                    {question.isCorrect ? '✓ Correct' : '✗ Incorrect'}
                  </span>
                )}
              </div>

              {/* Question Content - Vertical Layout */}
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                {/* Math Problem Section */}
                <div className="math-problem" ref={(el) => mathProblemRefs.current[question.id] = el} style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  fontFamily: 'monospace',
                  fontSize: '18px',
                  alignItems: 'flex-end',
                  marginRight: '100px'
                }}>
                  {/* Multiplicand row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px' }}></div>
                    {padNumber(question.num1, Math.max(question.num1.length, question.num2.length)).split('').map((digit, digitIndex) => (
                      <div
                        key={`num1-${digitIndex}`}
                        style={{
                          width: '40px',
                          height: '50px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e8f4fd',
                          border: '2px solid #ddd',
                          borderRadius: '4px',
                          fontWeight: 'bold'
                        }}
                      >
                        {digit === ' ' ? '' : digit}
                      </div>
                    ))}
                  </div>

                  {/* Multiplier row with operator */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ 
                      width: '30px', 
                      textAlign: 'center',
                      fontSize: '24px',
                      fontWeight: 'bold'
                    }}>×</div>
                    {padNumber(question.num2, Math.max(question.num1.length, question.num2.length)).split('').map((digit, digitIndex) => (
                      <div
                        key={`num2-${digitIndex}`}
                        style={{
                          width: '40px',
                          height: '50px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#e8f4fd',
                          border: '2px solid #ddd',
                          borderRadius: '4px',
                          fontWeight: 'bold'
                        }}
                      >
                        {digit === ' ' ? '' : digit}
                      </div>
                    ))}
                  </div>

                  {/* Line */}
                  <div style={{ 
                    height: '2px', 
                    backgroundColor: '#333',
                    margin: '10px 0',
                    width: `${Math.max(question.num1.length, question.num2.length) * 40 + (Math.max(question.num1.length, question.num2.length) - 1) * 5}px`
                  }}></div>

                  {/* Answer row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <div style={{ width: '30px' }}></div>
                    {Array.from({ length: question.answerLength }, (_, index) => (
                      <div
                        key={`answer-${index}`}
                        style={{
                          width: '40px',
                          height: '50px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: question.isCorrect ? '#d4edda' : '#f8d7da',
                          border: '2px solid #ddd',
                          borderRadius: '4px',
                          fontWeight: 'bold',
                          color: question.isCorrect ? '#155724' : '#721c24'
                        }}
                      >
                        {(question.userAnswer || [])[index] || ''}
                      </div>
                    ))}
                  </div>
                  
                  {/* Virtual Keyboard - Only show for this question if it's active */}
                  {activeInput && activeInput.questionIndex === question.id && keyboardVisible && (
                    <VirtualKeyboard
                      onKeyPress={handleVirtualKeyPress}
                      onBackspace={handleVirtualBackspace}
                      onClear={handleVirtualClear}
                      position={keyboardPosition}
                      isVisible={keyboardVisible}
                    />
                  )}
                </div>

                {/* Step-by-Step Solution Section - At the Bottom */}
                {examCompleted && steps.length > 0 && (
                  <div className="solution-section" style={{ 
                    marginTop: '30px', 
                    padding: '20px', 
                    backgroundColor: '#f8f9fa', 
                    borderRadius: '8px', 
                    border: '1px solid #e9ecef',
                    marginLeft: '200px',
                    marginRight: '200px'
                  }}>
                    <h3 style={{ color: '#667eea', marginBottom: '15px' }}>Step-by-Step Solution</h3>
                    <div className="solution-steps">
                      {/* Problem and Steps Combined */}
                      <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                        <strong style={{ color: '#667eea' }}>Problem: {question.num1} × {question.num2} = ?</strong>
                        <div style={{ marginTop: '10px', fontFamily: 'monospace', fontSize: '14px' }}>
                          {steps.map((step, stepIndex) => (
                            <div key={stepIndex} style={{ marginBottom: '5px' }}>
                              <span style={{ color: '#333', backgroundColor: '#e8f4fd', padding: '2px 4px', borderRadius: '3px' }}>{step.calculation}</span>
                              <span style={{ color: '#155724', backgroundColor: '#d4edda', padding: '2px 4px', borderRadius: '3px' }}> = {step.partialProduct}</span>
                              <span style={{ color: '#666', fontSize: '12px' }}> ({step.explanation.split(': ')[1]})</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Partial Products and Addition Combined */}
                      <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                        <strong style={{ color: '#667eea' }}>Partial Products with Place Values:</strong>
                        <div style={{ marginTop: '10px', fontFamily: 'monospace', fontSize: '14px' }}>
                          {partialProducts.map((partial, partialIndex) => (
                            <div key={partialIndex} style={{ marginBottom: '3px', textAlign: 'right' }}>
                              {partial.value.toString().padStart(finalResult.toString().length, ' ')}
                              <span style={{ color: '#666', fontSize: '12px', marginLeft: '10px' }}>
                                ({partial.original} × 10^{partial.placeValue})
                              </span>
                            </div>
                          ))}
                          <div style={{ 
                            borderTop: '2px solid #333', 
                            marginTop: '5px', 
                            paddingTop: '5px',
                            textAlign: 'right',
                            fontWeight: 'bold'
                          }}>
                            {finalResult}
                          </div>
                        </div>
                      </div>

                      {/* Final Answer */}
                      <div style={{ padding: '12px', backgroundColor: '#667eea', color: 'white', borderRadius: '6px', fontWeight: 'bold', textAlign: 'center' }}>
                        Final Answer: {finalResult}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
                 })}
       </div>

    </div>
  );
};

export default MultiplicationExam; 